import { Alert, Container, Grid, Typography } from '@mui/material';
import { DocumentData } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router';

import LessonCard from 'components/dashboard/LessonCard/LessonCard';
import LessonCardSkeleton from 'components/dashboard/LessonCard/LessonCardSkeleton';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs/HeaderBreadcrumbs';
import Page from 'components/Page/Page';
import SkeletonWrapper from 'components/SkeletonWrapper/SkeletonWrapper';
import { getGroupModuleLastLessonNumber, getModule, getModuleLessons } from 'external/firebase/controllers/db';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';
import { PATH_DASHBOARD } from 'routes/paths';

const Module: React.FunctionComponent = () => {
    const { profile } = useAuth();
    const { module: moduleId } = useParams();
    const { translate } = useLocales();

    const [module, setModule] = useState<DocumentData>({});
    const [lessons, setLessons] = useState<DocumentData>([]);
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);

    const fetchModule = async (moduleId: string) => {
        try {
            const module = await getModule(moduleId);
            setModule(module);
        } catch (err) {
            console.error(err);
            // FIXME:handle error
        }
    };

    const fetchModuleLessons = async (groupId: string, moduleId: string) => {
        try {
            const lastLessonNumber = await getGroupModuleLastLessonNumber(groupId, moduleId);
            const lesson = await getModuleLessons(moduleId, lastLessonNumber);
            setLessons(lesson);
            setError(undefined);
        } catch (err) {
            console.error(err);
            // FIXME: handle error
            // setError(translate(err.code));
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllModuleLessons = async (moduleId: string) => {
        try {
            const lesson = await getModuleLessons(moduleId, Infinity);
            setLessons(lesson);
            setError(undefined);
        } catch (err) {
            console.error(err);
            // FIXME: handle error
            // setError(translate(resLesson.code));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (moduleId) {
            fetchModule(moduleId);
        }
    }, [moduleId]);

    useEffect(() => {
        if (profile?.groups && moduleId) {
            fetchModuleLessons(profile.groups[0], moduleId);
        } else if ((profile?.role === 'admin' || profile?.role === 'teacher') && moduleId) {
            fetchAllModuleLessons(moduleId);
        }
    }, [profile?.groups, moduleId]);

    return (
        <Page title={`${translate('lessons')} | CodeAcademy Kids`}>
            <Container maxWidth='xl'>
                <HeaderBreadcrumbs
                    heading={module?.title}
                    links={[
                        { name: translate('dashboard'), href: PATH_DASHBOARD.root },
                        { name: translate('modules'), href: PATH_DASHBOARD.general.modules },
                        { name: module?.title, href: '' },
                    ]}
                />

                {isLoading && (
                    <Grid container spacing={3} justifyContent='center'>
                        <SkeletonWrapper copies={5}>
                            <Grid item xs={12} md={10}>
                                <LessonCardSkeleton />
                            </Grid>
                        </SkeletonWrapper>
                    </Grid>
                )}

                {error && (
                    <Alert severity='error'>
                        {translate('error_fetching_lessons')} {translate('please_try_again_or_contact_admin')}
                    </Alert>
                )}

                {!isLoading && lessons.length > 4 && (
                    <>
                        <Grid container spacing={3} justifyContent='center'>
                            <Grid item xs={12} md={10} key={'latest-sections'}>
                                <Typography variant='h6'>{translate('lastest_lesson')}</Typography>
                            </Grid>
                            <Grid item xs={12} md={10} key={lessons[lessons.length - 1].id}>
                                <LessonCard lesson={lessons[lessons.length - 1]} />
                            </Grid>
                            <Grid item xs={12} md={10} key={'all-lessons'}>
                                <Typography variant='h6' my={3}>
                                    {translate('all_lessons')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </>
                )}

                {!isLoading &&
                    (lessons.length > 0 ? (
                        <>
                            <Grid container spacing={3} justifyContent='center'>
                                {lessons.map(lesson => (
                                    <Grid item xs={12} md={10} key={lesson.id}>
                                        <LessonCard lesson={lesson} />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    ) : (
                        <Grid container spacing={3} justifyContent='center'>
                            <Typography variant='h6' sx={{ px: 5, mt: 10, mb: 5 }}>
                                {translate('no_lessons_found')}
                            </Typography>
                        </Grid>
                    ))}
                <Outlet />
            </Container>
        </Page>
    );
};

export default Module;
